/**
 * GlowOrbBackground
 * Reusable ambient glow orb layer for marketing pages.
 * Renders absolutely-positioned blur circles in the gold / purple / emerald brand palette.
 * Usage: place as the first child of a `relative overflow-hidden` container.
 */

interface OrbConfig {
  color: 'gold' | 'purple' | 'emerald' | 'blue';
  size: number; // px
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  opacity?: number;
}

interface GlowOrbBackgroundProps {
  orbs?: OrbConfig[];
  className?: string;
}

const COLOR_MAP = {
  gold: 'rgba(242, 203, 110, 1)',
  purple: 'rgba(117, 78,  155, 1)',
  emerald: 'rgba(74,  222, 128, 1)',
  blue: 'rgba(59,  130, 246, 1)',
};

const getColor = (color: 'gold' | 'purple' | 'emerald' | 'blue'): string => {
  if (color === 'gold') return COLOR_MAP.gold;
  if (color === 'purple') return COLOR_MAP.purple;
  if (color === 'emerald') return COLOR_MAP.emerald;
  if (color === 'blue') return COLOR_MAP.blue;
  return COLOR_MAP.purple;
};

const defaultOrbs: OrbConfig[] = [
  { color: 'purple', size: 600, top: '-200px', left: '-200px', opacity: 0.15 },
  { color: 'gold', size: 400, top: '100px', right: '-150px', opacity: 0.12 },
  { color: 'purple', size: 300, bottom: '-100px', right: '20%', opacity: 0.1 },
];

export const GlowOrbBackground = ({
  orbs = defaultOrbs,
  className = '',
}: GlowOrbBackgroundProps) => {
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      {orbs.map((orb, i) => (
        <div
          key={i}
          className="glow-orb absolute rounded-full"
          style={{
            width: `${orb.size}px`,
            height: `${orb.size}px`,
            background: getColor(orb.color),
            opacity: orb.opacity ?? 0.12,
            top: orb.top,
            bottom: orb.bottom,
            left: orb.left,
            right: orb.right,
            filter: 'blur(120px)',
          }}
        />
      ))}
    </div>
  );
};

export default GlowOrbBackground;
